import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Zap, TrendingUp, Shield } from "lucide-react";

const TRUST_POINTS = [
  { icon: BarChart3, text: "Professional buyer comparison tables" },
  { icon: Zap, text: "Top panels for every use case" },
  { icon: TrendingUp, text: "RV, camping, generators & off-grid" },
  { icon: Shield, text: "Transparent Amazon affiliate disclosure" },
];

const CELL_ROWS = 4;
const CELL_COLS = 5;

export default function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950/80 to-slate-900">
      {/* Gold grid lines */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, #faba00, #faba00 1px, transparent 1px, transparent 72px),
                            repeating-linear-gradient(90deg, #faba00, #faba00 1px, transparent 1px, transparent 72px)`,
        }}
      />
      {/* Glow orb */}
      <div className="absolute top-0 right-1/4 w-[600px] h-[600px] rounded-full blur-[140px] pointer-events-none" style={{ background: 'rgba(250,185,0,0.06)' }} />

      <div className="relative max-w-7xl mx-auto px-4 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent/15 border border-accent/25 rounded-full mb-6">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span className="text-[11px] font-mono font-semibold text-accent tracking-[0.15em] uppercase">2026 Guide — Updated</span>
            </div>

            <h1 className="font-heading font-bold text-4xl lg:text-5xl xl:text-6xl leading-[1.05] mb-6 text-white">
              Highest Efficiency{" "}
              <span className="text-accent">Solar Cell</span>{" "}
              <br className="hidden sm:block" />
              <span className="text-white/90">&amp; Solar Panel Guide</span>
            </h1>

            <p className="text-lg text-slate-300/90 mb-8 max-w-lg leading-relaxed">
              Compare the most efficient solar cell technologies and top-rated panels for RVs, camping, home backup, solar generators, and off-grid power systems.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <Link
                to="/best-high-efficiency-solar-panels"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-accent text-slate-950 font-heading font-bold text-sm rounded-xl hover:bg-amber-400 transition-all hover:shadow-lg hover:-translate-y-0.5 min-h-[52px]"
                style={{ '--tw-shadow-color': 'rgba(250,185,0,0.35)' }}
              >
                Compare High-Efficiency Solar Panels <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/solar-panel-calculator"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-white/20 text-white text-sm font-heading font-medium rounded-xl hover:bg-white/10 hover:border-white/30 transition-all min-h-[52px]"
              >
                Use the Solar Panel Calculator
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {TRUST_POINTS.map((point, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <point.icon className="w-4 h-4 text-accent shrink-0" />
                  <span className="text-sm text-slate-400">{point.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative w-full max-w-sm">
              {/* Ambient glow */}
              <div className="absolute inset-0 rounded-3xl blur-3xl" style={{ background: 'rgba(250,185,0,0.12)' }} />

              {/* Main card */}
              <div
                className="relative backdrop-blur-md rounded-2xl p-6 shadow-2xl"
                style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: 'rgba(250,185,0,0.75)' }}>Solar Panel Analysis</span>
                  <span className="px-2 py-0.5 text-[10px] font-mono rounded-md" style={{ background: 'rgba(250,185,0,0.2)', color: '#faba00' }}>2026</span>
                </div>

                {/* Solar cell grid */}
                <div
                  className="grid gap-1.5 mb-6"
                  style={{ gridTemplateColumns: `repeat(${CELL_COLS}, 1fr)` }}
                >
                  {Array.from({ length: CELL_ROWS * CELL_COLS }).map((_, i) => (
                    <div
                      key={i}
                      className="aspect-square rounded-sm"
                      style={{
                        background: `linear-gradient(145deg, #0d2356 0%, #081730 100%)`,
                        borderTop: `1px solid rgba(250,185,0,${i % 3 === 0 ? '0.3' : '0.1'})`,
                        borderLeft: `1px solid rgba(250,185,0,${i % 4 === 0 ? '0.2' : '0.07'})`,
                        boxShadow: i % 7 === 0 ? '0 0 8px rgba(250,185,0,0.28)' : 'none',
                      }}
                    />
                  ))}
                </div>

                {/* Stats */}
                <div className="space-y-2">
                  {[
                    { label: "CELL EFFICIENCY", value: "24.2%", gold: true },
                    { label: "PANEL OUTPUT", value: "400W", gold: false },
                    { label: "TECHNOLOGY", value: "TOPCon / IBC", gold: false },
                    { label: "REAL-WORLD OUTPUT", value: "High Performance", gold: false },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="flex justify-between items-center py-2 last:border-0"
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                    >
                      <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: 'rgba(148,163,184,0.6)' }}>{stat.label}</span>
                      <span className={`font-heading font-bold text-sm ${stat.gold ? 'text-accent' : 'text-white'}`}>
                        {stat.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating badges */}
              <div
                className="absolute -top-3 -right-3 lg:-right-5 rounded-xl px-3 py-2.5 shadow-xl"
                style={{ background: '#1e293b', border: '1px solid rgba(250,185,0,0.3)', boxShadow: '0 8px 32px rgba(250,185,0,0.12)' }}
              >
                <p className="text-[9px] font-mono uppercase tracking-wider mb-0.5" style={{ color: 'rgba(148,163,184,0.7)' }}>Top Efficiency</p>
                <p className="font-heading font-bold text-accent text-xl leading-none">24.2%</p>
              </div>
              <div
                className="absolute -bottom-3 -left-3 lg:-left-5 rounded-xl px-3 py-2.5 shadow-xl"
                style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <p className="text-[9px] font-mono uppercase tracking-wider mb-0.5" style={{ color: 'rgba(148,163,184,0.7)' }}>Leading Tech</p>
                <p className="font-heading font-bold text-white text-sm leading-none">Monocrystalline</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fade to light background */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
}